import { EllipsisTooltip } from 'App/Map/PanelDetails/OverviewCharts/EllipsisTooltip'
import type { CurrentValuesDataInfo } from 'App/Map/PanelDetails/OverviewCharts/OverviewColumns'
import {
  lastDays,
  lastHours,
  nextDays,
  nextHours,
  periodTitle,
  sinceFirstOf,
} from 'App/Map/PanelDetails/OverviewCharts/_utils/columnTitles'
import type { TValueGroupColumns } from 'App/Map/PanelDetails/OverviewCharts/_utils/types'
import { valueColumn } from 'App/Map/PanelDetails/OverviewCharts/_utils/valueColumn'
import { IconWindDirection } from 'components/icons/IconWindDirection'
import { translate } from 'i18n/i18n'
import { isNil } from 'lodash'
import { unitConverter } from 'utils/unitConverter/unitConverter'

const wetHoursColumn = (dataIndex: string, period: string) =>
  valueColumn({
    dataIndex,
    title: periodTitle(translate.phrases.banyanApp('Wet Hours'), period),
    width: 225,
    converter: unitConverter.leafWetnessHours,
  })

const rainColumn = (dataIndex: string, period: string) =>
  valueColumn({
    dataIndex,
    title: periodTitle(translate.phrases.banyanApp('Rain'), period),
    width: 175,
    converter: unitConverter.precipitation,
  })

export const environmentColumns: Record<string, TValueGroupColumns> = {
  air_temperature: {
    leadingColumn: 'pointName',
    getColumns: () => [
      valueColumn({
        dataIndex: 'temperature_in_c',
        title: translate.phrases.banyanApp('Temperature'),
        width: 150,
        converter: unitConverter.temperatureInCanopy,
      }),
      valueColumn({
        dataIndex: 'temperatureInversion_aboveVsIn_c',
        title: translate.phrases.banyanApp('Temperature Inversion'),
        width: 200,
        converter: unitConverter.temperatureInversion,
      }),
    ],
  },
  wind: {
    leadingColumn: 'pointName',
    getColumns: () => [
      {
        title: <EllipsisTooltip fullText={translate.phrases.banyanApp('Wind')} />,
        dataIndex: 'windSpeed_mps',
        key: 'windSpeed_mps',
        width: 150,
        render: (_: number, record: CurrentValuesDataInfo) => {
          if (isNil(record.windSpeed_mps)) return translate.phrases.templates('-')

          return (
            <>
              {unitConverter.windSpeed(record.windSpeed_mps).valueWithSuffix()}
              <IconWindDirection windDirection={record.windDirection_degrees} />
            </>
          )
        },
      },
    ],
  },
  humidity: {
    leadingColumn: 'pointName',
    getColumns: () => [
      valueColumn({
        dataIndex: 'humidity_in_pct',
        title: translate.phrases.banyanApp('Humidity'),
        width: 200,
        converter: unitConverter.humidityInCanopy,
      }),
    ],
  },
  leaf_wetness: {
    leadingColumn: 'pointName',
    getColumns: () => [
      wetHoursColumn('leafWetness_last24HoursSum_wetHours', lastHours(24)),
      wetHoursColumn('leafWetness_next24HoursSum_wetHours', nextHours(24)),
    ],
  },
  precipitation: {
    leadingColumn: 'pointName',
    getColumns: ({ isSouthernHemisphere }) => [
      rainColumn('precipitation_following7DaysSum_mm', nextDays(7)),
      rainColumn('precipitation_last24HoursSum_mm', lastHours(24)),
      rainColumn('precipitation_next24HoursSum_mm', nextHours(24)),
      rainColumn('precipitation_previous7DaysSum_mm', lastDays(7)),
      ...(isSouthernHemisphere
        ? [
            rainColumn('precipitation_sinceApr1Sum_mm', sinceFirstOf('Apr')),
            rainColumn('precipitation_sinceJul1Sum_mm', sinceFirstOf('Jul')),
          ]
        : [
            rainColumn('precipitation_sinceJan1Sum_mm', sinceFirstOf('Jan')),
            rainColumn('precipitation_sinceOct1Sum_mm', sinceFirstOf('Oct')),
          ]),
    ],
  },
  dew_point: {
    leadingColumn: 'pointName',
    getColumns: () => [
      valueColumn({
        dataIndex: 'dewPoint_in_c',
        title: translate.phrases.banyanApp('Dew Point'),
        width: 200,
        converter: unitConverter.dewPointInCanopy,
      }),
    ],
  },
  wet_bulb: {
    leadingColumn: 'pointName',
    getColumns: () => [
      valueColumn({
        dataIndex: 'wetBulb_in_c',
        title: translate.phrases.banyanApp('Wet Bulb'),
        width: 200,
        converter: unitConverter.wetBulbInCanopy,
      }),
    ],
  },
}
